import { apiClient } from "./client";

async function get(path: string) {
  const { data } = await apiClient.get(path);
  return data;
}
async function post(path: string, body?: unknown, headers?: Record<string, string>) {
  const { data } = await apiClient.post(path, body ?? {}, headers ? { headers } : undefined);
  return data;
}
async function put(path: string, body?: unknown) {
  const { data } = await apiClient.put(path, body ?? {});
  return data;
}

/** Download a CSV/blob export and trigger a browser save. */
export async function downloadExport(path: string, filename: string) {
  const res = await apiClient.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Merchant API surface — hits the NestJS backend at /v1/merchant/*, /v1/payin, /v1/payout. */
export const merchantApi = {
  async login(client_id: string, client_secret: string) {
    const { data } = await apiClient.post("/v1/auth/token", { client_id, client_secret });
    return data as { success: boolean; access_token: string; token_type: string; expires_in: number };
  },

  // Dashboard login: email + password, then Google Authenticator TOTP if enabled.
  async dashboardLogin(email: string, password: string) {
    const { data } = await apiClient.post("/v1/auth/merchant/login", { email, password });
    return data as {
      success: boolean;
      requires_2fa?: boolean;
      totp_setup_required?: boolean;
      login_token?: string;
      access_token?: string;
    };
  },
  async verify2fa(login_token: string, code: string) {
    const { data } = await apiClient.post("/v1/auth/merchant/2fa", { login_token, code });
    return data as { success: boolean; access_token: string };
  },
  async changePasswordFirst(change_token: string, new_password: string) {
    const { data } = await apiClient.post("/v1/auth/merchant/change-password", { change_token, new_password });
    return data as { success: boolean; access_token: string };
  },
  setup2fa: () => post("/v1/merchant/2fa/setup"),
  enable2fa: (code: string) => post("/v1/merchant/2fa/enable", { code }),
  disable2fa: (code: string) => post("/v1/merchant/2fa/disable", { code }),
  changePassword: (body: { current_password?: string; new_password: string }) =>
    put("/v1/merchant/password", body),
  logoutAll: () => post("/v1/merchant/logout-all"),

  // ── multi-country accounts ──
  // One merchant ID per market, linked under a single login.
  accounts: () => get("/v1/merchant/accounts"),
  switchAccount: (merchant_id: string) =>
    post("/v1/merchant/accounts/switch", { merchant_id }),

  // ── reads ──
  overview: () => get("/v1/merchant/overview"),
  profile: () => get("/v1/merchant/profile"),
  balance: () => get("/v1/merchant/balance"),
  /**
   * The markets this account may process in.
   *
   * Read-only. Only the platform enables a market, so there is no companion
   * mutation here and the UI must not offer one.
   */
  countries: () => get("/v1/merchant/countries"),
  balanceAsOf: (date: string) => get(`/v1/merchant/balance?as_of=${encodeURIComponent(date)}`),
  wallet: () => get("/v1/merchant/wallet"),
  credentials: () => get("/v1/merchant/credentials"),
  settlements: (qs = "") => get(`/v1/merchant/settlements${qs}`),
  payinOrders: (qs = "") => get(`/v1/merchant/orders/payin${qs}`),
  payoutOrders: (qs = "") => get(`/v1/merchant/orders/payout${qs}`),
  payinOrder: (id: string) => get(`/v1/merchant/orders/payin/${id}`),
  payoutOrder: (id: string) => get(`/v1/merchant/orders/payout/${id}`),
  webhookConfig: () => get("/v1/merchant/webhook"),
  webhookDeliveries: (qs = "") => get(`/v1/merchant/webhook/deliveries${qs}`),

  // ── payments (create) ──
  createPayin: (body: unknown) =>
    post("/v1/payin/create", body, { "x-idempotency-key": crypto.randomUUID() }),
  createPayout: (body: unknown) =>
    post("/v1/payout/create", body, { "x-idempotency-key": crypto.randomUUID() }),

  // ── order actions ──
  overridePayin: (id: string, body: unknown) =>
    post(`/v1/merchant/orders/payin/${id}/override-status`, body),
  overridePayout: (id: string, body: unknown) =>
    post(`/v1/merchant/orders/payout/${id}/override-status`, body),

  // ── credentials / security ──
  rotateCredentials: () => post("/v1/merchant/credentials/rotate"),
  updateSecurity: (ip_whitelist: string[]) => put("/v1/merchant/security", { ip_whitelist }),
  setLimits: (min_payin_amount: number | null, max_payin_amount: number | null) =>
    put("/v1/merchant/limits", { min_payin_amount, max_payin_amount }),

  // ── withdrawals (settled-balance payout requests) ──
  withdrawals: (qs = "") => get(`/v1/merchant/withdrawals${qs}`),
  requestWithdrawal: (body: unknown) => post("/v1/merchant/withdrawals", body),

  // ── webhook management ──
  updateWebhookConfig: (body: unknown) => put("/v1/merchant/webhook", body),
  rotateWebhookSecret: () => post("/v1/merchant/webhook/rotate-secret"),
  sendTestWebhook: () => post("/v1/merchant/webhook/test"),
  retryWebhookDelivery: (id: string) => post(`/v1/merchant/webhook/deliveries/${id}/retry`),

  // ── disputes ──
  disputes: (qs = "") => get(`/v1/merchant/disputes${qs}`),
  dispute: (id: string) => get(`/v1/merchant/disputes/${id}`),
  createDispute: (body: unknown) => post("/v1/merchant/disputes", body),
  addDisputeMessage: (id: string, body: unknown) => post(`/v1/merchant/disputes/${id}/messages`, body),
  presignDisputeAttachment: (id: string, body: unknown) => post(`/v1/merchant/disputes/${id}/attachments/presign`, body),
  registerDisputeAttachment: (id: string, body: unknown) => post(`/v1/merchant/disputes/${id}/attachments`, body),
  disputeAttachmentUrl: (id: string, attId: string) => get(`/v1/merchant/disputes/${id}/attachments/${attId}/url`),

  // ── support tickets ──
  tickets: (qs = "") => get(`/v1/merchant/tickets${qs}`),
  ticket: (id: string) => get(`/v1/merchant/tickets/${id}`),
  createTicket: (body: unknown) => post("/v1/merchant/tickets", body),
  ticketReply: (id: string, body: unknown) => post(`/v1/merchant/tickets/${id}/messages`, body),
  ticketStatus: (id: string, body: unknown) => post(`/v1/merchant/tickets/${id}/status`, body),
  ticketRate: (id: string, body: unknown) => post(`/v1/merchant/tickets/${id}/rate`, body),

  // ── notifications (bell) ──
  notifications: (unread = false) => get(`/v1/merchant/notifications${unread ? "?unread=1" : ""}`),
  notificationRead: (id: string) => post(`/v1/merchant/notifications/${id}/read`),
  notificationReadAll: () => post("/v1/merchant/notifications/read-all"),
};

/** Upload a file to a presigned S3 PUT URL (browser → S3 directly). */
export async function uploadToPresigned(url: string, file: File) {
  const res = await fetch(url, { method: "PUT", body: file, headers: { "Content-Type": file.type } });
  if (!res.ok) throw new Error("Upload failed");
}
